import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import {
  MapPin,
  Phone,
  Calendar,
  Edit,
  Trash2,
  Heart,
  Share2,
  ShoppingBag,
  Copy,
  Navigation,
  Flower2,
  CreditCard,
  Clock
} from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import Modal from '../../components/common/Modal';
import ConfirmModal from '../../components/common/ConfirmModal';
import ErrorState from '../../components/common/ErrorState';
import { Skeleton } from '../../components/common/Skeleton';
import ShareButtons from '../../components/obituary/ShareButtons';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { fetchObituaries } from '../../utils/api';
import { getDepartureDday, ddayToneClass, copyToClipboard } from '../../components/obituary/obituaryUtils';

/**
 * ObituaryDetailPage - 부고 상세 페이지
 * 부고 정보, 지도/길찾기, 방명록, 온라인 헌화, 부의금 계좌, 장례 일정 표시
 */
const ObituaryDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showToast } = useToast();
  const { user } = useAuth();

  const [obituary, setObituary] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // 온라인 헌화 (mock 상태)
  const [flowerCount, setFlowerCount] = useState(0);
  const [hasOffered, setHasOffered] = useState(false);

  // 방명록 (로컬 상태 mock)
  const [guestbook, setGuestbook] = useState([]);
  const [condolenceName, setCondolenceName] = useState('');
  const [condolenceMessage, setCondolenceMessage] = useState('');

  // 부고 데이터 불러오기
  const loadObituary = useCallback(async () => {
    try {
      setIsLoading(true);
      setIsError(false);
      const obituaries = await fetchObituaries();
      const found = obituaries.find((o) => o.id === parseInt(id, 10));

      if (!found) {
        showToast('부고를 찾을 수 없습니다.', 'error');
        navigate('/obituaries');
        return;
      }

      setObituary(found);
      setIsFavorite(found.isFavorite || false);
      // mock 초기 헌화 수 (id 기반 의사 난수)
      setFlowerCount(120 + (found.id % 7) * 13);
    } catch (error) {
      console.error('Failed to load obituary:', error);
      setIsError(true);
      showToast('부고를 불러오는데 실패했습니다.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate, showToast]);

  useEffect(() => {
    loadObituary();
  }, [loadObituary]);

  // 사용자 이름 자동 채움
  useEffect(() => {
    if (user?.name) setCondolenceName(user.name);
  }, [user]);

  const handleToggleFavorite = () => {
    setIsFavorite((prev) => !prev);
    showToast(
      isFavorite ? '즐겨찾기에서 제거되었습니다.' : '즐겨찾기에 추가되었습니다.',
      'success'
    );
  };

  const handleEdit = () => navigate(`/obituaries/${id}/edit`);
  const handleDelete = () => setShowDeleteModal(true);

  const confirmDelete = () => {
    setIsDeleting(true);
    // Mock 삭제
    console.log('Deleting obituary:', id);
    setTimeout(() => {
      setIsDeleting(false);
      setShowDeleteModal(false);
      showToast('부고가 삭제되었습니다.', 'success');
      navigate('/obituaries');
    }, 600);
  };

  const handleSendWreath = () =>
    navigate('/wreaths', { state: { obituaryId: id, obituary } });

  const handleShare = () => setShowShareModal(true);

  // 온라인 헌화
  const handleOfferFlower = () => {
    if (hasOffered) {
      showToast('이미 헌화하셨습니다.', 'info');
      return;
    }
    setFlowerCount((c) => c + 1);
    setHasOffered(true);
    showToast('국화 한 송이를 헌화했습니다.', 'success');
  };

  // 연락처 / 계좌 복사
  const handleCopy = async (text, label) => {
    const ok = await copyToClipboard(text);
    showToast(ok ? `${label}이(가) 복사되었습니다.` : '복사에 실패했습니다.', ok ? 'success' : 'error');
  };

  // 방명록 작성
  const handleSubmitCondolence = (e) => {
    e.preventDefault();
    if (!condolenceMessage.trim()) {
      showToast('조문 메시지를 입력해주세요.', 'error');
      return;
    }
    const entry = {
      id: Date.now(),
      name: condolenceName.trim() || '익명',
      message: condolenceMessage.trim(),
      createdAt: new Date().toISOString(),
    };
    setGuestbook((prev) => [entry, ...prev]);
    setCondolenceMessage('');
    showToast('조문 메시지가 등록되었습니다.', 'success');
  };

  // 로딩 상태 - 스켈레톤
  if (isLoading) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
          <Skeleton className="h-10 w-32" />
          <div className="rounded-lg border border-accent-200 bg-white p-6">
            <Skeleton className="mx-auto mb-3 h-8 w-40" />
            <Skeleton className="mx-auto mb-6 h-4 w-56" />
            <Skeleton className="mb-3 h-5 w-3/4" />
            <Skeleton className="mb-3 h-5 w-2/3" />
            <Skeleton className="h-5 w-1/2" />
          </div>
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
      </MainLayout>
    );
  }

  // 에러 상태
  if (isError) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <ErrorState
            title="부고를 불러오지 못했습니다"
            message="잠시 후 다시 시도해주세요."
            onRetry={loadObituary}
          />
        </div>
      </MainLayout>
    );
  }

  if (!obituary) return null;

  // 날짜 포맷팅
  const formattedDepartureDate = format(
    new Date(obituary.departureDate),
    'yyyy년 MM월 dd일 (E)',
    { locale: ko }
  );

  const dday = getDepartureDday(obituary.departureDate);
  const isOwner = user && user.id === obituary.userId;
  const hasCoords = obituary.lat != null && obituary.lng != null;

  // 길찾기 딥링크
  const encodedAddr = encodeURIComponent(obituary.funeralAddress);
  const encodedName = encodeURIComponent(obituary.funeralHome);
  const naverUrl = hasCoords
    ? `https://map.naver.com/v5/directions/-/${obituary.lng},${obituary.lat},${encodedName}`
    : `https://map.naver.com/v5/search/${encodedAddr}`;
  const kakaoUrl = hasCoords
    ? `https://map.kakao.com/link/to/${encodedName},${obituary.lat},${obituary.lng}`
    : `https://map.kakao.com/?q=${encodedAddr}`;
  const tmapUrl = hasCoords
    ? `https://apis.openapi.sk.com/tmap/app/routes?goalname=${encodedName}&goalx=${obituary.lng}&goaly=${obituary.lat}`
    : `https://tmap.life/`;

  // 장례 일정 타임라인 (사망일/발인일 기반 mock)
  const timeline = [
    { label: '별세', date: obituary.deceasedDeathDate },
    { label: '입관', date: obituary.deceasedDeathDate, note: '안내 예정' },
    { label: '성복', date: obituary.deceasedDeathDate, note: '안내 예정' },
    { label: '발인', date: obituary.departureDate, time: obituary.departureTime },
  ];

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Action Buttons - Top */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <Button variant="outline" onClick={() => navigate('/obituaries')} className="sm:w-auto">
            ← 목록으로
          </Button>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={handleToggleFavorite}
              className="flex items-center gap-2 flex-1 sm:flex-initial"
            >
              <Heart size={20} className={isFavorite ? 'fill-red-500 text-red-500' : ''} />
              <span className="hidden sm:inline">즐겨찾기</span>
            </Button>

            <Button
              variant="outline"
              onClick={handleShare}
              className="flex items-center gap-2 flex-1 sm:flex-initial"
            >
              <Share2 size={20} />
              <span className="hidden sm:inline">공유</span>
            </Button>

            {isOwner && (
              <>
                <Button
                  variant="outline"
                  onClick={handleEdit}
                  className="flex items-center gap-2 flex-1 sm:flex-initial"
                >
                  <Edit size={20} />
                  <span className="hidden sm:inline">수정</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDelete}
                  className="flex items-center gap-2 text-red-600 hover:bg-red-50 flex-1 sm:flex-initial"
                >
                  <Trash2 size={20} />
                  <span className="hidden sm:inline">삭제</span>
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Obituary Content */}
        <Card className="mb-8">
          {/* Header */}
          <div className="text-center border-b pb-6 mb-6">
            {dday && (
              <span
                className={`mb-3 inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ${ddayToneClass(dday.tone)}`}
              >
                {dday.label}
              </span>
            )}
            {obituary.portraitUrl && (
              <img
                src={obituary.portraitUrl}
                alt={`故 ${obituary.deceasedName} 영정`}
                className="mx-auto mb-4 h-32 w-28 rounded object-cover border border-accent-200"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            <h1 className="text-3xl font-bold text-primary-900 mb-3">
              故 {obituary.deceasedName}
            </h1>
            <p className="text-gray-600">
              {obituary.chiefMourner} {obituary.chiefMournerRelationship}의 {obituary.relationship}
            </p>
          </div>

          {/* Info Grid */}
          <div className="space-y-4">
            {/* 빈소 정보 */}
            <div className="flex items-start gap-3">
              <MapPin className="text-primary-600 mt-1 flex-shrink-0" size={20} />
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">빈소</h3>
                <p className="text-gray-700">
                  {obituary.funeralHome} {obituary.funeralRoom}
                </p>
                <p className="text-sm text-gray-600 mt-1">{obituary.funeralAddress}</p>
              </div>
            </div>

            {/* 발인 정보 */}
            <div className="flex items-start gap-3">
              <Calendar className="text-primary-600 mt-1 flex-shrink-0" size={20} />
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">발인</h3>
                <p className="text-gray-700">
                  {formattedDepartureDate} {obituary.departureTime}
                </p>
              </div>
            </div>

            {/* 상주 정보 */}
            <div className="flex items-start gap-3 border-t pt-4">
              <Phone className="text-primary-600 mt-1 flex-shrink-0" size={20} />
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">상주 연락처</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-gray-700">
                      {obituary.chiefMourner} ({obituary.chiefMournerRelationship})
                    </span>
                    <span className="flex items-center gap-2">
                      <a
                        href={`tel:${obituary.chiefMournerPhone}`}
                        className="text-primary-600 hover:underline"
                      >
                        {obituary.chiefMournerPhone}
                      </a>
                      <button
                        type="button"
                        onClick={() => handleCopy(obituary.chiefMournerPhone, '연락처')}
                        className="text-gray-400 hover:text-primary-600"
                        aria-label="상주 연락처 복사"
                      >
                        <Copy size={16} />
                      </button>
                    </span>
                  </div>

                  {/* 추가 상주 (B6: phone 있을 때만 표시) */}
                  {obituary.additionalMourners &&
                    obituary.additionalMourners.length > 0 &&
                    obituary.additionalMourners.map((mourner, index) => (
                      <div key={index} className="flex items-center justify-between gap-2">
                        <span className="text-gray-700">
                          {mourner.name} ({mourner.relationship})
                        </span>
                        {mourner.phone ? (
                          <span className="flex items-center gap-2">
                            <a
                              href={`tel:${mourner.phone}`}
                              className="text-primary-600 hover:underline"
                            >
                              {mourner.phone}
                            </a>
                            <button
                              type="button"
                              onClick={() => handleCopy(mourner.phone, '연락처')}
                              className="text-gray-400 hover:text-primary-600"
                              aria-label={`${mourner.name} 연락처 복사`}
                            >
                              <Copy size={16} />
                            </button>
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            </div>

            {/* 부의금 계좌 */}
            {obituary.donationAccount && (
              <div className="flex items-start gap-3 border-t pt-4">
                <CreditCard className="text-primary-600 mt-1 flex-shrink-0" size={20} />
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">부의금 계좌</h3>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-gray-700 break-all">{obituary.donationAccount}</span>
                    <button
                      type="button"
                      onClick={() => handleCopy(obituary.donationAccount, '계좌번호')}
                      className="flex items-center gap-1 text-sm text-primary-600 hover:underline flex-shrink-0"
                    >
                      <Copy size={16} /> 복사
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* 장례 일정 타임라인 */}
        <Card className="mb-8">
          <h2 className="text-xl font-bold text-primary-900 mb-4 flex items-center gap-2">
            <Clock size={20} /> 장례 일정
          </h2>
          <ol className="relative border-l border-accent-200 ml-2 space-y-5">
            {timeline.map((item, idx) => (
              <li key={idx} className="ml-4">
                <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full bg-primary-500" />
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-semibold text-gray-900">{item.label}</span>
                  <span className="text-sm text-gray-600">
                    {item.date
                      ? format(new Date(item.date), 'MM월 dd일', { locale: ko })
                      : '안내 예정'}
                    {item.time ? ` ${item.time}` : ''}
                  </span>
                  {item.note && <span className="text-xs text-gray-400">({item.note})</span>}
                </div>
              </li>
            ))}
          </ol>
        </Card>

        {/* Map + 길찾기 */}
        <Card className="mb-8">
          <h2 className="text-xl font-bold text-primary-900 mb-4">장례식장 위치</h2>
          <div className="bg-gradient-to-br from-accent-100 to-accent-200 rounded-lg h-64 flex flex-col items-center justify-center text-center px-4">
            <MapPin className="text-primary-500 mb-2" size={36} />
            <p className="font-semibold text-gray-800">{obituary.funeralHome}</p>
            <p className="text-sm text-gray-600 mt-1">{obituary.funeralAddress}</p>
            {hasCoords && (
              <p className="text-xs text-gray-400 mt-2">
                좌표: {obituary.lat}, {obituary.lng}
              </p>
            )}
          </div>

          {/* 길찾기 딥링크 */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <a
              href={naverUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 rounded-lg border border-accent-300 py-2 text-sm font-medium text-gray-700 hover:bg-accent-50"
            >
              <Navigation size={16} /> 네이버 길찾기
            </a>
            <a
              href={kakaoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 rounded-lg border border-accent-300 py-2 text-sm font-medium text-gray-700 hover:bg-accent-50"
            >
              <Navigation size={16} /> 카카오 길찾기
            </a>
            <a
              href={tmapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 rounded-lg border border-accent-300 py-2 text-sm font-medium text-gray-700 hover:bg-accent-50"
            >
              <Navigation size={16} /> 티맵 길찾기
            </a>
          </div>
        </Card>

        {/* 온라인 헌화 */}
        <Card className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <Flower2 className="text-primary-600" size={28} />
              <div>
                <h2 className="text-lg font-bold text-primary-900">온라인 헌화</h2>
                <p className="text-sm text-gray-600">
                  지금까지 <span className="font-semibold text-primary-700">{flowerCount.toLocaleString('ko-KR')}</span>송이의 국화가 헌화되었습니다.
                </p>
              </div>
            </div>
            <Button
              variant={hasOffered ? 'outline' : 'primary'}
              onClick={handleOfferFlower}
              disabled={hasOffered}
              className="flex items-center gap-2"
            >
              <Flower2 size={18} />
              {hasOffered ? '헌화 완료' : '헌화하기'}
            </Button>
          </div>
        </Card>

        {/* 조문 방명록 */}
        <Card className="mb-8">
          <h2 className="text-xl font-bold text-primary-900 mb-4">조문 방명록</h2>

          <form onSubmit={handleSubmitCondolence} className="space-y-3 mb-6">
            <input
              type="text"
              value={condolenceName}
              onChange={(e) => setCondolenceName(e.target.value)}
              placeholder="이름 (선택)"
              className="w-full px-4 py-2 rounded-lg border border-accent-300 bg-white text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <textarea
              value={condolenceMessage}
              onChange={(e) => setCondolenceMessage(e.target.value)}
              rows="3"
              maxLength={300}
              placeholder="삼가 고인의 명복을 빕니다."
              className="w-full px-4 py-2 rounded-lg border border-accent-300 bg-white text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-400">{condolenceMessage.length}/300</span>
              <Button type="submit" variant="primary" size="sm">
                조문 남기기
              </Button>
            </div>
          </form>

          {guestbook.length === 0 ? (
            <p className="text-center text-sm text-gray-500 py-6">
              아직 등록된 조문 메시지가 없습니다. 첫 조문을 남겨주세요.
            </p>
          ) : (
            <ul className="space-y-3">
              {guestbook.map((entry) => (
                <li key={entry.id} className="rounded-lg bg-accent-50 border border-accent-200 p-3">
                  <div className="flex justify-between items-baseline mb-1">
                    <span className="font-semibold text-gray-900">{entry.name}</span>
                    <span className="text-xs text-gray-400">
                      {format(new Date(entry.createdAt), 'MM.dd HH:mm', { locale: ko })}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{entry.message}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* CTA - 화환 보내기 */}
        <Card className="bg-primary-50 border-primary-200">
          <div className="text-center">
            <ShoppingBag className="mx-auto mb-3 text-primary-600" size={40} />
            <h2 className="text-xl font-bold text-primary-900 mb-2">조화 보내기</h2>
            <p className="text-gray-700 mb-4">
              소중한 분의 마지막 가시는 길에<br />
              정성스런 조화를 보내주세요.
            </p>
            <Button variant="primary" onClick={handleSendWreath} className="px-8">
              조화 상품 보기
            </Button>
          </div>
        </Card>

        {/* Delete Confirm Modal */}
        <ConfirmModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={confirmDelete}
          title="부고 삭제"
          message="정말로 이 부고를 삭제하시겠습니까? 삭제된 부고는 복구할 수 없습니다."
          confirmLabel="삭제"
          confirmVariant="danger"
          loading={isDeleting}
        />

        {/* Share Modal */}
        <Modal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          title="부고 공유하기"
        >
          <div className="py-4">
            <ShareButtons obituary={obituary} onClose={() => setShowShareModal(false)} />
          </div>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default ObituaryDetailPage;
